import { receiveNumber, receiveString } from "../../../../controllers/adapters/receives"

/**
 * Formatea `2026-10-05` -> `05/10/2026`. Si el valor ya es texto
 * (`5 d'octubre de 2026`) se devuelve tal cual.
 */
const formatoFecha = (valor: any) => {

    const texto = receiveString(valor)

    if (!texto) return ''

    if (texto.includes(' ')) return texto

    const [anio, mes, dia] = texto.split('-')

    return anio && mes && dia ? `${dia}/${mes}/${anio}` : texto
}

// Origen del WS d'Activitats (https://adminactivitats.arescoop.es) para montar el
// enlace de generación de PDF cuando la factura no trae `fa_ref_pdf`:
// `../plantillas/generar_factura.php` -> `{origen}/plantillas/generar_factura.php`
const origenWS = (() => {

    try {

        const base = process.env.REACT_APP_WEB_SERVICE_ACTIVIDADES || ''

        return base ? new URL(base).origin : ''

    } catch {

        return ''
    }
})()

/**
 * Mapea una factura del WS d'Activitats (prefijos `fa_*` + `ac_actividad` +
 * `id_factura`).
 */
const schemaFactura = (item: any = {}) => {

    const id = receiveString(item.id_factura || item.fa_id_factura)
    const idInscripcion = receiveString(item.fa_id_inscripcion)

    const enlaceGeneracion = id
        ? `${origenWS}/plantillas/generar_factura.php?id_factura=${encodeURIComponent(id)}&id_inscripcion=${encodeURIComponent(idInscripcion)}`
        : ''

    return {
        id,
        idInscripcion,
        numFactura: receiveString(item.fa_num_factura),
        actividad: receiveString(item.ac_actividad),
        fecha: formatoFecha(item.fa_fecha_factura),
        tipo: receiveString(item.tipo_factura),
        refPdf: receiveString(item.fa_ref_pdf),
        enlaceGeneracion,
    }
}

/**
 * Extrae el listado de facturas de la respuesta del WS. Una fila es válida si
 * trae `id_factura` o `fa_num_factura`.
 */
const extraerFacturas = (data: any): any[] => {

    if (!data) return []

    const esFila = (item: any) => item && typeof item === 'object' && (item.id_factura || item.fa_num_factura)

    // { data: { data: [...], total, per_page, current_page, last_page } }
    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data) && Array.isArray(data.data?.data)) {

        return data.data.data.filter(esFila)
    }

    // { data: [...] }
    if (Array.isArray(data?.data)) return data.data.filter(esFila)

    if (Array.isArray(data)) return data.filter(esFila)

    return []
}

const FacturasAdapters = {

    // GET {base}/integraWeb.php?accion=obtener_facturas&empresa_id=...&cif=...&buscar=...&numero_factura=...&tipo_factura=...&page=N
    // Facturas del sistema d'Activitats (integra-web). `empresa_id` identifica la
    // empresa del backoffice y `cif` el NIF/CIF (igual que en listar_inscripciones).
    // Filtros: `buscar` (texto libre), `numero_factura` y `tipo_factura` (código,
    // ver `obtener_tiposfacturas`). La respuesta es paginada en el objeto `data`
    // (data, total, per_page, current_page, last_page) y cada fila trae
    // `id_factura`, `fa_num_factura`, `ac_actividad`, `fa_fecha_factura`,
    // `tipo_factura` y `fa_ref_pdf` (opcional).
    OBTENER_FACTURAS: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'obtener_facturas',
                    empresa_id: item.empresa_id ?? item.empresaId ?? '',
                    cif: item.cif || '',
                    buscar: item.buscar || '',
                    numero_factura: item.numero_factura ?? item.numeroFactura ?? '',
                    tipo_factura: item.tipo_factura ?? item.tipoFactura ?? '',
                    page: item.page || 1,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaFactura,

            adapter: function (data: any) {

                const items = extraerFacturas(data).map((item: any) => this.schema(item))

                const paginacion = data?.data || data || {}

                return {
                    items,
                    paginacion: {
                        total: receiveNumber(paginacion.total),
                        per_page: receiveNumber(paginacion.per_page),
                        page: receiveNumber(paginacion.current_page),
                        last_page: receiveNumber(paginacion.last_page),
                    }
                }
            }
        },
    },

    // GET {base}/integraWeb.php?accion=obtener_tiposfacturas
    // Catálogo de tipos de factura: el WS devuelve un objeto `id -> etiqueta`
    // que se normaliza a un array de opciones [{ value, label }] para el filtro.
    GET_TIPOS_FACTURA: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'obtener_tiposfacturas',
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                return {
                    tipos: item && typeof item === 'object' && !Array.isArray(item)
                        ? Object.entries(item).map(([value, label]: [string, any]) => ({
                            value: receiveString(value),
                            label: receiveString(label),
                        }))
                        : [],
                }
            },

            adapter: function (data: any) {

                return this.schema(data?.data ?? data)
            }
        },
    },
}

export {
    FacturasAdapters
}